import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

// Mock data - would come from real metrics API
const mockHistogramData = [
  { bucket: '0-50ms', count: 1234 },
  { bucket: '50-100ms', count: 2341 },
  { bucket: '100-200ms', count: 1876 },
  { bucket: '200-500ms', count: 654 },
  { bucket: '500ms+', count: 123 }
];

const mockThroughputData = [
  { time: '00:00', requests: 145, successful: 142 },
  { time: '00:05', requests: 167, successful: 164 },
  { time: '00:10', requests: 134, successful: 131 },
  { time: '00:15', requests: 198, successful: 195 },
  { time: '00:20', requests: 176, successful: 174 }
];

const mockConnectionsData = [
  { time: '00:00', websocket: 45, http: 123 },
  { time: '00:05', websocket: 52, http: 145 },
  { time: '00:10', websocket: 38, http: 134 },
  { time: '00:15', websocket: 61, http: 167 },
  { time: '00:20', websocket: 48, http: 156 }
];

interface PercentileCardProps {
  percentile: string;
  value: string;
  status?: 'success' | 'warning' | 'error';
}

function PercentileCard({ percentile, value, status = 'success' }: PercentileCardProps) {
  const statusStyles = {
    success: 'border-green-200 bg-green-50',
    warning: 'border-yellow-200 bg-yellow-50',
    error: 'border-red-200 bg-red-50'
  };

  return (
    <div className={`p-3 rounded-lg border ${statusStyles[status]}`}>
      <div className="text-xs text-muted-foreground">{percentile}</div>
      <div className="text-lg font-mono font-bold">{value}</div>
    </div>
  );
}

interface MetricChartProps {
  data: any[];
  lines: Array<{ key: string; color: string; label: string }>;
  height: number;
  showLegend?: boolean;
}

function MetricChart({ data, lines, height, showLegend }: MetricChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="time" 
          tick={{ fontSize: 10 }}
          stroke="#6b7280"
        />
        <YAxis 
          tick={{ fontSize: 10 }}
          stroke="#6b7280"
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '12px'
          }}
        />
        {lines.map((line) => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            stroke={line.color}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function PerformanceMetrics() {
  return (
    <div className="space-y-4">
      {/* Response Time Distribution */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Response Time Distribution</CardTitle>
            <Select defaultValue="1h">
              <SelectTrigger className="w-24 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5m">5 min</SelectItem>
                <SelectItem value="15m">15 min</SelectItem>
                <SelectItem value="1h">1 hour</SelectItem>
                <SelectItem value="24h">24 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4 mb-4">
            <PercentileCard percentile="P50" value="45ms" />
            <PercentileCard percentile="P75" value="89ms" />
            <PercentileCard percentile="P90" value="156ms" />
            <PercentileCard percentile="P95" value="234ms" />
            <PercentileCard percentile="P99" value="567ms" status="warning" />
          </div>
          
          {/* Histogram */}
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mockHistogramData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="bucket" 
                  tick={{ fontSize: 10 }}
                  stroke="#6b7280"
                />
                <YAxis 
                  tick={{ fontSize: 10 }}
                  stroke="#6b7280"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Throughput & Concurrency */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Throughput</CardTitle>
          </CardHeader>
          <CardContent>
            <MetricChart
              data={mockThroughputData}
              lines={[
                { key: 'requests', color: '#3b82f6', label: 'Requests/sec' },
                { key: 'successful', color: '#10b981', label: 'Success/sec' }
              ]}
              height={200}
              showLegend
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Active Connections</CardTitle>
          </CardHeader>
          <CardContent>
            <MetricChart
              data={mockConnectionsData}
              lines={[
                { key: 'websocket', color: '#8b5cf6', label: 'WebSocket' },
                { key: 'http', color: '#f59e0b', label: 'HTTP' }
              ]}
              height={200}
              showLegend
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}